import React, { useState, useRef, useEffect } from 'react';
import { 
  Home, ShoppingCart, User, Gift, Package, LayoutDashboard, LogOut, ChevronDown,
  Shield, Box, ClipboardList, BarChart3, CreditCard, Truck, Users, Settings, Palette, Eye,
  Store, MapPin, Navigation, TrendingUp, ClipboardCheck, Copy, Menu, Scan, Printer,
  ChevronRight, ChevronLeft, Layers, Receipt, Database, CheckCircle, Book, HelpCircle, FileText, DollarSign, Star, QrCode
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import NotificationCenter from './NotificationCenter';
import ShareButton from './ShareButton';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';

const iconMap = {
  Home,
  ShoppingCart,
  User,
  Gift,
  Package,
  LayoutDashboard,
  Shield,
  Box,
  ClipboardList,
  ClipboardCheck,
  BarChart3,
  CreditCard,
  Truck,
  Users,
  Eye,
  Settings,
  Palette,
  Store,
  MapPin,
  Navigation,
  TrendingUp,
  Copy,
  Menu,
  Scan,
  Receipt,
  Printer,
  Layers,
  Database,
  CheckCircle,
  Book,
  HelpCircle,
  FileText,
  DollarSign,
};

// Grouped admin menu structure
const adminMenuGroups = [
  {
    label: 'Quick Access',
    items: [
      { id: 'pos-cashier', name: 'POS / CASHIER', icon: 'ShoppingCart', highlight: 'emerald' },
      { id: 'pos-sales-report', name: 'POS Sales Report', icon: 'Receipt', highlight: 'blue' },
    ]
  },
  {
    label: 'Operations',
    items: [
      { id: 'admin-dashboard', name: 'Dashboard', icon: 'BarChart3' },
      { id: 'admin-reports', name: 'Reports', icon: 'TrendingUp' },
      { id: 'order-pickup-admin', name: 'ORDER PICKUP', icon: 'Package', highlight: 'emerald' },
      { id: 'print-center', name: 'Print Center', icon: 'Printer', highlight: 'blue' },
      { id: 'picking-portal', name: 'Picking Portal', icon: 'Package', highlight: 'amber' },
      { id: 'picking-list', name: 'Picking List', icon: 'ClipboardCheck' },
      { id: 'general-overseer', name: 'Overseer', icon: 'Eye' },
    ]
  },
  {
    label: 'Inventory',
    items: [
      { id: 'admin-products', name: 'Products', icon: 'Box' },
      { id: 'identical-products', name: 'Duplicates', icon: 'Copy' },
    ]
  },
  {
    label: 'Orders & Payments',
    items: [
      { id: 'admin-orders', name: 'Orders', icon: 'ClipboardList' },
      { id: 'payment-confirmation-portal', name: 'Confirm Payments', icon: 'CheckCircle', highlight: 'green' },
      { id: 'payment-portal', name: 'Payments', icon: 'CreditCard' },
      { id: 'stripe-settings', name: 'Stripe', icon: 'CreditCard' },
    ]
  },
  {
    label: 'Marketing',
    items: [
      { id: 'promo-notifications', name: 'Promotions', icon: 'Gift' },
      { id: 'branding-settings', name: 'Branding', icon: 'Palette' },
      { id: 'footer-settings', name: 'Footer', icon: 'FileText', highlight: 'purple' },
    ]
  },
  {
    label: 'System',
    items: [
      { id: 'device-settings', name: 'Devices', icon: 'Settings' },
      { id: 'user-management', name: 'Users', icon: 'Users' },
      { id: 'role-management', name: 'Roles', icon: 'Shield' },
      { id: 'drivers-dashboard', name: 'Drivers', icon: 'Truck' },
      { id: 'driver-fees', name: 'Driver Fees', icon: 'DollarSign', highlight: 'green' },
      { id: 'system-maintenance', name: 'System', icon: 'Settings' },
      { id: 'backup-management', name: 'Backup & Restore', icon: 'Database', highlight: 'blue' },
      { id: 'operations-manual', name: 'Operations Manual', icon: 'Book', highlight: 'purple' },
      { id: 'superadmin-portal', name: 'Super Admin', icon: 'Shield', highlight: 'red' },
    ]
  },
];

const Navbar = ({ navItems, adminNavItems, activeNav, setActiveNav, user, onSignInClick, onSignOut, branding, onCustomerGuideClick, onWriteReviewClick }) => {
  const { t } = useLanguage();
  const isAdmin = user?.isAdmin || user?.role === 'admin';
  const isDriver = user?.role === 'driver';
  const isCustomer = user && !isAdmin && !isDriver;
  
  const defaultName = "UPLANDS FOOD MARKET";
  const defaultPrimaryColor = "#0a1628";

  // Scrollable admin menu refs and state
  const adminMenuRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll position
  const checkScroll = () => {
    if (adminMenuRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = adminMenuRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Update scroll state on mount and resize
  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [isAdmin]);

  // Scroll admin menu left/right
  const scrollAdminMenu = (direction) => {
    if (adminMenuRef.current) {
      const scrollAmount = 200;
      adminMenuRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 300);
    }
  };

  // Flatten all admin menu items for scrollable display
  const allAdminItems = adminMenuGroups.flatMap(group => group.items);

  const handleNavigateToOrders = (orderId = null) => {
    setActiveNav('orders');
    // If we have a specific order ID, we could pass it through state
    // For now, just navigate to orders
  };

  return (
    <nav className="bg-white border-b border-[#0a1628]/20 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo Only - No Business Name */}
          <div 
            className="flex items-center gap-2 cursor-pointer flex-shrink-0"
            onClick={() => setActiveNav('shop')}
          >
            {branding?.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt="Logo" 
                className="object-contain"
                style={{ 
                  maxWidth: `${branding?.logo_width || 250}px`, 
                  maxHeight: `${branding?.logo_height || 65}px` 
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className={`w-10 h-10 rounded-lg flex items-center justify-center bg-[#0a1628] ${branding?.logo_url ? 'hidden' : ''}`}
            >
              <ShoppingCart className="w-6 h-6 text-white" />
            </div>
            {isAdmin && (
              <Badge className="bg-[#0a1628] text-white ml-2">ADMIN</Badge>
            )}
            {isDriver && (
              <Badge className="bg-[#0a1628]/80 text-white ml-2">DRIVER</Badge>
            )}
          </div>

          {/* Nav Items */}
          <div className="hidden md:flex items-center gap-1">
            {/* Regular Nav Items - color based on role */}
            {navItems.map((item) => {
              const Icon = iconMap[item.icon];
              const isDriverNav = isDriver && ['driver-onboarding', 'driver-navigation', 'order-status'].includes(item.id);
              const isCustomerNav = isCustomer && item.id === 'order-tracking';
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveNav(item.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    activeNav === item.id
                      ? 'bg-[#0a1628] text-white border border-[#0a1628]'
                      : 'text-[#0a1628]/80 hover:bg-[#0a1628]/10 border border-transparent'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.name}
                </button>
              );
            })}
          </div>

          {/* Admin Scrollable Menu - Moved to User Section end */}

          {/* User Section */}
          <div className="flex items-center gap-2">
            {/* ORDER PICKUP Button - Always visible for logged-in customers on mobile */}
            {user && (isCustomer || isDriver) && (
              <button
                onClick={() => setActiveNav('order-pickup-user')}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-colors border border-emerald-200 md:hidden"
                title="Order Pickup / QR Scan"
                data-testid="mobile-order-pickup-btn"
              >
                <QrCode className="w-4 h-4" />
                <span className="text-sm font-medium">Pickup</span>
              </button>
            )}
            
            {/* Help Guide Button - Visible for all users */}
            <button
              onClick={onCustomerGuideClick}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors border border-blue-200"
              title="Customer Help Guide"
              data-testid="help-guide-nav-btn"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="hidden sm:inline text-sm font-medium">Help</span>
            </button>

            {/* Language Selector */}
            <LanguageSelector variant="dropdown" showLabel={false} />
            
            {/* Share Button */}
            <ShareButton />
            
            {/* Notification Center */}
            {user && (
              <NotificationCenter 
                user={user} 
                onNavigateToOrders={handleNavigateToOrders}
              />
            )}
            
            {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 rounded-full bg-[#0a1628]/10 hover:bg-[#0a1628]/20 transition-colors border border-[#0a1628]/20">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm bg-[#0a1628]`}>
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="font-medium text-[#0a1628] hidden sm:block">{user.name}</span>
                  {isAdmin && <Shield className="w-4 h-4 text-[#0a1628]" />}
                  <ChevronDown className="w-4 h-4 text-[#0a1628]/70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white border-[#0a1628]/20">
                <div className="px-3 py-2">
                  <p className="font-medium text-[#0a1628]">{user.name}</p>
                  <p className="text-sm text-[#0a1628]/60">{user.email}</p>
                  {isAdmin && (
                    <Badge className="bg-[#0a1628] text-white mt-1">Administrator</Badge>
                  )}
                  {isDriver && (
                    <Badge className="bg-[#0a1628]/80 text-white mt-1">Driver</Badge>
                  )}
                </div>
                <DropdownMenuSeparator className="bg-[#0a1628]/20" />
                
                {/* Common items for all logged-in users */}
                <DropdownMenuItem onClick={() => setActiveNav('profile')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                  <User className="w-4 h-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveNav('my-orders')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                  <Package className="w-4 h-4 mr-2" />
                  My Orders
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveNav('gift-cards')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                  <Gift className="w-4 h-4 mr-2" />
                  Gift Cards
                </DropdownMenuItem>

                {/* Customer-specific items */}
                {isCustomer && (
                  <>
                    <DropdownMenuSeparator className="bg-[#0a1628]/20" />
                    <div className="px-2 py-1 text-xs font-semibold text-[#0a1628]/50 uppercase">Customer</div>
                    <DropdownMenuItem onClick={() => setActiveNav('in-store-shopping')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                      <Store className="w-4 h-4 mr-2" />
                      In-Store Shopping
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveNav('order-tracking')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                      <MapPin className="w-4 h-4 mr-2" />
                      Live Order Tracking
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveNav('order-pickup-user')} className="cursor-pointer text-emerald-600 hover:bg-emerald-50">
                      <QrCode className="w-4 h-4 mr-2" />
                      Order Pickup / QR Scan
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onCustomerGuideClick} className="cursor-pointer text-blue-600 hover:bg-blue-50">
                      <HelpCircle className="w-4 h-4 mr-2" />
                      Help Guide
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onWriteReviewClick} className="cursor-pointer text-yellow-600 hover:bg-yellow-50">
                      <Star className="w-4 h-4 mr-2" />
                      Write a Review
                    </DropdownMenuItem>
                  </>
                )}

                {/* Driver-specific items */}
                {isDriver && (
                  <>
                    <DropdownMenuSeparator className="bg-[#0a1628]/20" />
                    <div className="px-2 py-1 text-xs font-semibold text-[#0a1628]/50 uppercase">Driver</div>
                    <DropdownMenuItem onClick={() => setActiveNav('driver-onboarding')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                      <Truck className="w-4 h-4 mr-2" />
                      Driver Setup
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveNav('driver-navigation')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                      <Navigation className="w-4 h-4 mr-2" />
                      Navigation
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveNav('order-status')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                      <ClipboardList className="w-4 h-4 mr-2" />
                      Order Status
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveNav('order-pickup-user')} className="cursor-pointer text-emerald-600 hover:bg-emerald-50">
                      <QrCode className="w-4 h-4 mr-2" />
                      Pickup / QR Scan
                    </DropdownMenuItem>
                  </>
                )}
                
                {/* Admin Menu Items */}
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator className="bg-[#0a1628]/20" />
                    <div className="px-2 py-1 text-xs font-semibold text-[#0a1628]/50 uppercase">Admin</div>
                    <DropdownMenuItem onClick={() => setActiveNav('admin-dashboard')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveNav('admin-products')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                      <Box className="w-4 h-4 mr-2" />
                      Manage Products
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveNav('admin-orders')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                      <ClipboardList className="w-4 h-4 mr-2" />
                      Manage Orders
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveNav('payment-portal')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Payment Portal
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveNav('user-management')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                      <Users className="w-4 h-4 mr-2" />
                      User Management
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveNav('role-management')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                      <Shield className="w-4 h-4 mr-2" />
                      Role Management
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveNav('drivers-dashboard')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                      <Truck className="w-4 h-4 mr-2" />
                      Drivers
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveNav('system-maintenance')} className="cursor-pointer text-[#0a1628]/90 hover:bg-[#0a1628]/10">
                      <Settings className="w-4 h-4 mr-2" />
                      System Maintenance
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#0a1628]/20" />
                    <div className="px-2 py-1 text-xs font-semibold text-[#0a1628]/50 uppercase">New Features</div>
                    <DropdownMenuItem 
                      onClick={() => setActiveNav('general-overseer')} 
                      className="cursor-pointer bg-[#0a1628]/10 text-[#0a1628] hover:bg-[#0a1628]/20 font-medium"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      General Overseer
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setActiveNav('branding-settings')} 
                      className="cursor-pointer bg-[#0a1628]/10 text-[#0a1628] hover:bg-[#0a1628]/20 font-medium"
                    >
                      <Palette className="w-4 h-4 mr-2" />
                      Branding Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setActiveNav('promo-notifications')} 
                      className="cursor-pointer bg-[#0a1628]/10 text-[#0a1628] hover:bg-[#0a1628]/20 font-medium"
                    >
                      <Gift className="w-4 h-4 mr-2" />
                      Promotions
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setActiveNav('stripe-settings')} 
                      className="cursor-pointer bg-[#0a1628]/10 text-[#0a1628] hover:bg-[#0a1628]/20 font-medium"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Stripe Configuration
                    </DropdownMenuItem>
                  </>
                )}
                
                <DropdownMenuSeparator className="bg-[#0a1628]/20" />
                <DropdownMenuItem onClick={onSignOut} className="cursor-pointer text-red-600 hover:bg-red-50">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button 
              onClick={onSignInClick}
              className="bg-[#0a1628] hover:bg-[#0a1628]/90 text-white rounded-full px-6"
            >
              Sign In
            </Button>
          )}
          </div>
        </div>
        
        {/* Admin Scrollable Menu - Second Row (Mobile & Desktop) */}
        {isAdmin && (
          <div className="flex items-center gap-2 py-2 border-t border-[#0a1628]/10">
            {/* Left scroll arrow */}
            {canScrollLeft && (
              <button
                onClick={() => scrollAdminMenu('left')}
                className="flex-shrink-0 p-1.5 rounded-full bg-[#0a1628] hover:bg-[#0a1628]/80 text-white transition-colors shadow-md"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            
            {/* Scrollable admin menu container */}
            <div
              ref={adminMenuRef}
              onScroll={checkScroll}
              className="flex items-center gap-1.5 overflow-x-auto flex-1 scroll-smooth pb-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
            >
              {allAdminItems.map((item) => {
                const Icon = iconMap[item.icon];
                const isActive = activeNav === item.id;
                const highlightClasses = {
                  'emerald': 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm',
                  'blue': 'bg-blue-500 text-white hover:bg-blue-600 shadow-sm',
                  'amber': 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm',
                  'green': 'bg-green-500 text-white hover:bg-green-600 shadow-sm',
                };
                
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveNav(item.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                      isActive
                        ? 'bg-[#0a1628] text-white shadow-md ring-2 ring-[#0a1628]/30'
                        : item.highlight
                          ? highlightClasses[item.highlight]
                          : 'bg-gray-100 text-[#0a1628]/80 hover:bg-[#0a1628]/10'
                    }`}
                    data-testid={`admin-nav-${item.id}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {item.name}
                  </button>
                );
              })}
            </div>
            
            {/* Right scroll arrow */}
            {canScrollRight && (
              <button
                onClick={() => scrollAdminMenu('right')}
                className="flex-shrink-0 p-1.5 rounded-full bg-[#0a1628] hover:bg-[#0a1628]/80 text-white transition-colors shadow-md"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
